import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Box, Button } from "@mui/material";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import {
  loanFieldConfiguration,
  loanInitialValues,
} from "../../../formConfigs/Fleet/loanFieldConfig";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import LenderPopup from "../LenderPopup/LenderPopup";

export const fetchLenders = async (token: string | null | undefined) => {
  const response = await axios.get(`${apiVariable}/getAllLenders`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.map((item: any) => ({
    label: `${item.name}`,
    id: item.id.toString(),
  }));
};

const LoanPopup = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: lendersData } = useQuery({
    queryKey: ["lenders"],
    queryFn: () => fetchLenders(token),
    enabled: !!open,
  });

  const loanMutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/loan`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      setOpen(false);
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to submit loan");
    },
  });

  const submitHandler = (data: any) => {
    const submitData = {
      ...data,
      lenderId: getId(data.lenderId),
    };
    queryClient.invalidateQueries({ queryKey: ["assets"] });
    queryClient.invalidateQueries({ queryKey: ["loans"] });
    loanMutation.mutate(submitData);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Create Loan
      </Button>
      <GenericFormPopup
        formFields={loanFieldConfiguration}
        initialValues={loanInitialValues}
        autocompleteData={{ lenders: lendersData }}
        title="Loan Form"
        onSubmit={submitHandler}
        open={open}
        onClose={() => setOpen(false)}
        loading={loanMutation.isPending}
        fieldComponents={{
          lenderId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <LenderPopup />
            </Box>
          ),
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage="Loan added successfully"
        errorMessage={errorMessage}
        handleClose={() => setStatus("idle")}
      />
    </>
  );
};

export default LoanPopup;
